import type { FavouriteSelection, Gallery, GalleryStatisticsResponse, Photo, Video } from '../lib/api';
import { formatBytes } from '../lib/utils';
import type { ReactNode, SyntheticEvent } from 'react';

interface GalleryStatisticsProps {
  gallery: Gallery;
  photos: Photo[];
  videos: Video[];
  selections: FavouriteSelection[];
  statistics: GalleryStatisticsResponse;
  onMediaError?: (event: SyntheticEvent<HTMLImageElement>) => void;
}

interface RankedPhoto {
  photo: Photo;
  clientCount: number;
}

interface CountedLabel {
  label: string;
  count: number;
}

export interface GalleryInsights {
  proofingClientCount: number;
  submittedSelectionCount: number;
  averageSubmittedSelectionSize: number;
  popularPhotos: RankedPhoto[];
  selectedScenes: CountedLabel[];
  recentProofing: FavouriteSelection[];
  originalSizeBytes: number;
  assignedCount: number;
  unassignedCount: number;
  assignmentPercentage: number;
  sceneDistribution: CountedLabel[];
}

export function calculateGalleryInsights(
  gallery: Gallery,
  photos: Photo[],
  videos: Video[],
  selections: FavouriteSelection[]
): GalleryInsights {
  const submitted = selections.filter((selection) => selection.status === 'SUBMITTED');
  const selectedClientCounts = new Map<string, number>();
  for (const selection of submitted) {
    for (const photoId of new Set(selection.photoIds)) {
      selectedClientCounts.set(photoId, (selectedClientCounts.get(photoId) ?? 0) + 1);
    }
  }
  const popularPhotos = photos
    .map((photo) => ({ photo, clientCount: selectedClientCounts.get(photo.photoId) ?? 0 }))
    .filter((entry) => entry.clientCount > 0)
    .sort((left, right) => right.clientCount - left.clientCount)
    .slice(0, 5);

  const scenes = gallery.scenes ?? [];
  const sceneSet = new Set(scenes);
  const photosById = new Map(photos.map((photo) => [photo.photoId, photo]));
  const selectedSceneCounts = new Map<string, number>();
  for (const selection of submitted) {
    for (const photoId of new Set(selection.photoIds)) {
      const photo = photosById.get(photoId);
      const scene = photo?.scene && sceneSet.has(photo.scene) ? photo.scene : 'Unassigned';
      selectedSceneCounts.set(scene, (selectedSceneCounts.get(scene) ?? 0) + 1);
    }
  }
  const selectedScenes = [
    ...scenes.map((scene) => ({ label: scene, count: selectedSceneCounts.get(scene) ?? 0 })),
    { label: 'Unassigned', count: selectedSceneCounts.get('Unassigned') ?? 0 }
  ]
    .filter((entry) => entry.count > 0)
    .sort((left, right) => right.count - left.count);

  const assignedCount = photos.filter((photo) => Boolean(photo.scene && sceneSet.has(photo.scene))).length;
  const mediaCount = photos.length + videos.length;
  const unassignedCount = mediaCount - assignedCount;
  const sceneDistribution = [
    ...scenes.map((scene) => ({ label: scene, count: photos.filter((photo) => photo.scene === scene).length })),
    { label: 'Unassigned', count: unassignedCount }
  ];

  return {
    proofingClientCount: selections.length,
    submittedSelectionCount: submitted.length,
    averageSubmittedSelectionSize: submitted.length
      ? submitted.reduce((total, selection) => total + selection.photoIds.length, 0) / submitted.length
      : 0,
    popularPhotos,
    selectedScenes,
    recentProofing: [...selections]
      .sort((left, right) => new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime())
      .slice(0, 5),
    originalSizeBytes: [...photos, ...videos].reduce((total, media) => total + media.sizeBytes, 0),
    assignedCount,
    unassignedCount,
    assignmentPercentage: mediaCount ? Math.round((assignedCount / mediaCount) * 100) : 0,
    sceneDistribution
  };
}

export function GalleryStatistics({ gallery, photos, videos, selections, statistics, onMediaError }: GalleryStatisticsProps) {
  const insights = calculateGalleryInsights(gallery, photos, videos, selections);
  const maxSceneMedia = Math.max(1, ...insights.sceneDistribution.map((entry) => entry.count));
  const maxSelectedScene = Math.max(1, ...insights.selectedScenes.map((entry) => entry.count));
  const hasDownloads = statistics.readyCount + statistics.pendingCount + statistics.failedCount > 0;
  const hasMedia = photos.length + videos.length > 0;

  return (
    <section className="grid gap-8" aria-labelledby="gallery-statistics-title">
      <div>
        <p className="text-sm uppercase tracking-[0.16em] text-ink/55">Client engagement</p>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <h2 id="gallery-statistics-title" className="text-3xl font-medium tracking-normal text-ink">Gallery Statistics</h2>
          <span className="inline-flex items-center rounded-full border border-ink/15 bg-bone/55 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-ink/60">
            Beta
          </span>
        </div>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-ink/65">
          A current snapshot of downloads, proofing, and media organization. Download activity covers the last {statistics.retentionDays} days.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-label="Engagement summary">
        <MetricCard label="Client downloads" value={statistics.readyCount} detail="Successfully prepared" />
        <MetricCard label="Proofing clients" value={insights.proofingClientCount} detail="Current client records" />
        <MetricCard label="Submitted selections" value={insights.submittedSelectionCount} detail="Final selections only" />
        <MetricCard
          label="Average selection size"
          value={formatAverage(insights.averageSubmittedSelectionSize)}
          detail="Across submitted selections"
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <StatisticsCard title="Download breakdown" description={`Public gallery exports from the last ${statistics.retentionDays} days.`}>
          {hasDownloads ? (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <CompactMetric label="Web / digital" value={statistics.webCount} />
                <CompactMetric label="Full quality" value={statistics.originalCount} />
                <CompactMetric label="Pending" value={statistics.pendingCount} />
                <CompactMetric label="Failed" value={statistics.failedCount} />
              </div>
              <p className="mt-5 text-sm text-ink/65">
                {statistics.latestReadyAt ? `Latest successful download ${formatDateTime(statistics.latestReadyAt)}.` : 'No successful downloads yet.'}
              </p>
            </>
          ) : <EmptyState>No client downloads in this retention window.</EmptyState>}
        </StatisticsCard>

        <StatisticsCard title="Recent download activity" description="The ten most recent anonymous client export requests.">
          {statistics.recent.length ? (
            <ul className="divide-y divide-ink/10">
              {statistics.recent.map((job, index) => (
                <li key={`${job.createdAt}-${index}`} className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
                  <div>
                    <p className="text-sm font-medium text-ink">{job.quality === 'web' ? 'Web / digital' : 'Full quality'}</p>
                    <p className="mt-1 text-xs text-ink/60">{job.photoCount} photo{job.photoCount === 1 ? '' : 's'} · {formatBytes(job.sizeBytes)} · {formatDateTime(job.createdAt)}</p>
                  </div>
                  <StatusPill status={job.status} />
                </li>
              ))}
            </ul>
          ) : <EmptyState>No recent download activity.</EmptyState>}
        </StatisticsCard>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <StatisticsCard title="Most-selected photos" description="Ranked by distinct clients with final submitted selections.">
          {insights.popularPhotos.length ? (
            <ol className="grid gap-3">
              {insights.popularPhotos.map(({ photo, clientCount }, index) => (
                <li key={photo.photoId} className="flex items-center gap-4 rounded-sm bg-bone/45 p-3">
                  <span className="w-5 shrink-0 text-center text-sm font-medium text-ink/55">{index + 1}</span>
                  {photo.url ? (
                    <img src={photo.url} alt="" className="h-14 w-14 shrink-0 rounded-sm object-cover" onError={onMediaError} />
                  ) : <div className="h-14 w-14 shrink-0 rounded-sm bg-smoke" aria-hidden="true" />}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ink">{photo.fileName}</p>
                    <p className="mt-1 text-xs text-ink/60">Selected by {clientCount} client{clientCount === 1 ? '' : 's'}</p>
                  </div>
                </li>
              ))}
            </ol>
          ) : <EmptyState>No final submitted selections to rank yet.</EmptyState>}
        </StatisticsCard>

        <StatisticsCard title="Most-selected scenes" description="Photo choices in current final selections.">
          {insights.selectedScenes.length ? (
            <BarList entries={insights.selectedScenes} maximum={maxSelectedScene} unit="selection" />
          ) : <EmptyState>{(gallery.scenes?.length ?? 0) ? 'No submitted scene selections yet.' : 'No scenes created yet.'}</EmptyState>}
        </StatisticsCard>
      </div>

      <StatisticsCard title="Recent proofing activity" description="Current proofing records; reopened selections are not counted as final.">
        {insights.recentProofing.length ? (
          <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {insights.recentProofing.map((selection) => (
              <li key={selection.clientKey} className="rounded-sm border border-ink/10 bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <p className="font-medium text-ink">{selection.clientName}</p>
                  <StatusPill status={selection.status} />
                </div>
                <p className="mt-3 text-sm text-ink/65">{selection.photoIds.length} photo{selection.photoIds.length === 1 ? '' : 's'} · {formatDateTime(selection.updatedAt)}</p>
              </li>
            ))}
          </ul>
        ) : <EmptyState>No proofing clients yet.</EmptyState>}
      </StatisticsCard>

      <div className="grid gap-6 xl:grid-cols-2">
        <StatisticsCard title="Media health" description="Original media and scene-assignment coverage.">
          {hasMedia ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <CompactMetric label="Photos" value={photos.length} />
              <CompactMetric label="Videos" value={videos.length} />
              <CompactMetric label="Original size" value={formatBytes(insights.originalSizeBytes)} />
              <CompactMetric label="Scenes" value={gallery.scenes?.length ?? 0} />
              <CompactMetric label="Assigned" value={`${insights.assignmentPercentage}%`} />
              <CompactMetric label="Unassigned" value={insights.unassignedCount} />
            </div>
          ) : <EmptyState>No media uploaded yet.</EmptyState>}
        </StatisticsCard>

        <StatisticsCard title="Scene distribution" description="Saved scene order, with unassigned media last.">
          {hasMedia && (gallery.scenes?.length ?? 0) > 0 ? (
            <BarList entries={insights.sceneDistribution} maximum={maxSceneMedia} unit="item" />
          ) : <EmptyState>{hasMedia ? 'No scenes created yet.' : 'No media to distribute across scenes.'}</EmptyState>}
        </StatisticsCard>
      </div>
    </section>
  );
}

function MetricCard({ label, value, detail }: { label: string; value: string | number; detail: string }) {
  return (
    <div className="rounded-sm border border-ink/10 bg-bone/40 p-5">
      <p className="text-sm text-ink/60">{label}</p>
      <p className="mt-3 text-3xl font-medium tracking-tight text-ink">{value}</p>
      <p className="mt-2 text-xs text-ink/55">{detail}</p>
    </div>
  );
}

function CompactMetric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-sm bg-bone/50 p-4">
      <p className="text-xs text-ink/55">{label}</p>
      <p className="mt-2 text-xl font-medium text-ink">{value}</p>
    </div>
  );
}

function StatisticsCard({ title, description, children }: { title: string; description: string; children: ReactNode }) {
  return (
    <section className="rounded-sm border border-ink/10 bg-white p-5 sm:p-6">
      <h3 className="text-xl font-medium text-ink">{title}</h3>
      <p className="mt-1 text-sm leading-6 text-ink/60">{description}</p>
      <div className="mt-5">{children}</div>
    </section>
  );
}

function BarList({ entries, maximum, unit }: { entries: CountedLabel[]; maximum: number; unit: string }) {
  return (
    <ul className="grid gap-4">
      {entries.map((entry) => (
        <li key={entry.label}>
          <div className="flex items-center justify-between gap-4 text-sm">
            <span className="truncate font-medium text-ink">{entry.label}</span>
            <span className="shrink-0 text-ink/60">{entry.count} {unit}{entry.count === 1 ? '' : 's'}</span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-bone" role="progressbar" aria-label={`${entry.label} ${unit}s`} aria-valuenow={entry.count} aria-valuemin={0} aria-valuemax={maximum}>
            <div className="h-full rounded-full bg-ink" style={{ width: `${entry.count ? Math.max(4, (entry.count / maximum) * 100) : 0}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

function StatusPill({ status }: { status: string }) {
  const normalized = status === 'PROCESSING' ? 'PENDING' : status;
  return (
    <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium uppercase tracking-wide ${
      normalized === 'READY'
        ? 'bg-emerald-100 text-emerald-800'
        : normalized === 'FAILED'
          ? 'bg-red-100 text-red-800'
          : normalized === 'SUBMITTED'
            ? 'bg-ink text-white'
            : 'bg-bone text-ink/70'
    }`}>
      {normalized === 'REOPENED' ? 'Reopened' : normalized.toLowerCase()}
    </span>
  );
}

function EmptyState({ children }: { children: ReactNode }) {
  return <p className="rounded-sm bg-bone/45 px-4 py-5 text-sm text-ink/65">{children}</p>;
}

function formatAverage(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('en-ZA', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
}
